import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { 
  getCompra, createCompra, updateCompra, addPagoToCompra,
  getProveedores, getCategorias, getLineasNegocio, getCentrosCosto, getCuentas, getArticulos
} from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { SearchableSelect } from '../components/ui/searchable-select';
import { TreeCategorySelect } from '../components/ui/tree-category-select';
import { 
  ChevronDown, ChevronUp, Plus, Trash2, Copy, X, FileText, Receipt, Save, CreditCard, PlusCircle, FileCheck, Calendar, CheckCircle, Clock, AlertCircle
} from 'lucide-react';
import { cn } from '../lib/utils';

const MEDIOS_PAGO = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TRANSFERENCIA', label: 'Transferencia' },
  { value: 'TARJETA', label: 'Tarjeta' },
  { value: 'YAPE', label: 'Yape' },
  { value: 'PLIN', label: 'Plin' },
  { value: 'DEPOSITO', label: 'Depósito' },
];

const IGV_RATE = 0.18;

const CompraFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const tipo = searchParams.get('tipo') || 'FACTURA_PROVEEDOR';
  const isEditing = !!id;
  
  const { empresaActual, monedas, tcHoy } = useApp();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Catálogos
  const [proveedores, setProveedores] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [articulos, setArticulos] = useState([]);
  const [lineasNegocio, setLineasNegocio] = useState([]);
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [modelosExternos, setModelosExternos] = useState([]);
  
  // Secciones colapsables
  const [categoriaOpen, setCategoriaOpen] = useState(true);
  const [articuloOpen, setArticuloOpen] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    proveedor_id: '',
    beneficiario_txt: '',
    terminos: '',
    tipo_doc: 'FACTURA',
    fecha: new Date().toISOString().split('T')[0],
    fecha_venc: '',
    nro_doc_full: '',
    moneda_id: '',
    nota: '',
    impuestosIncluidos: true  // Por defecto IGV incluido
  });
  
  // Tipos de documento
  const tiposDocumento = [
    { value: 'FACTURA', label: 'Factura' },
    { value: 'BOLETA', label: 'Boleta' },
    { value: 'NOTA_VENTA', label: 'Nota de Venta' }
  ];
  
  // Líneas de categoría (con IGV por defecto)
  const [lineasCategoria, setLineasCategoria] = useState([
    { id: 1, categoria_id: '', descripcion: '', linea_negocio_id: '', centro_costo_id: '', importe: '', conIgv: true }
  ]);
  
  // Líneas de artículo
  const [lineasArticulo, setLineasArticulo] = useState([]);
  
  // Pagos vinculados (para edición)
  const [pagosVinculados, setPagosVinculados] = useState([]);
  
  // Estado de CxP (para saber si está canjeada)
  const [cxpEstado, setCxpEstado] = useState(null);
  
  // Letras vinculadas
  const [letrasVinculadas, setLetrasVinculadas] = useState([]);
  
  // Modal de pago para gastos
  const [pagoDialogOpen, setPagoDialogOpen] = useState(false);
  const [pagoData, setPagoData] = useState({
    pagos: [{ cuenta_id: '', medio_pago: 'TRANSFERENCIA', monto: '', ref_pago: '' }]
  });
  const [pendingSubmit, setPendingSubmit] = useState({ createNew: false });
  
  // Modal agregar pago individual (para facturas)
  const [agregarPagoOpen, setAgregarPagoOpen] = useState(false);
  const [nuevoPagoFactura, setNuevoPagoFactura] = useState({
    cuenta_id: '',
    medio_pago: 'TRANSFERENCIA',
    monto: '',
    ref_pago: ''
  });
  const [savingPagoFactura, setSavingPagoFactura] = useState(false);
  
  // Modal crear proveedor
  const [nuevoProveedorOpen, setNuevoProveedorOpen] = useState(false);
  const [nuevoProveedor, setNuevoProveedor] = useState({
    tipo_doc: 'RUC', nro_doc: '', razon_social: '', telefono: '', email: '', direccion: '', dias_credito: 30
  });
  const [savingProveedor, setSavingProveedor] = useState(false);
  
  // Modal crear artículo
  const [nuevoArticuloOpen, setNuevoArticuloOpen] = useState(false);
  const [nuevoArticulo, setNuevoArticulo] = useState({
    codigo: '', nombre: '', tipo: 'PRODUCTO', unidad: 'UND', precio_ref: '', categoria_id: '', linea_negocio_id_def: ''
  });
  const [savingArticulo, setSavingArticulo] = useState(false);
  
  const esFactura = tipo === 'FACTURA_PROVEEDOR';
  const titulo = esFactura ? 'Factura de proveedor' : 'Gasto';
  const Icon = esFactura ? FileText : Receipt;

  // Cargar catálogos
  const loadCatalogos = useCallback(async () => {
    if (!empresaActual?.id) return;
    
    setLoading(true);
    try {
      const [provRes, catRes, artRes, lnRes, ccRes, cuentasRes, modelosRes, modelosProdRes] = await Promise.all([
        getProveedores(empresaActual.id),
        getCategorias(empresaActual.id),
        getArticulos(empresaActual.id),
        getLineasNegocio(empresaActual.id),
        getCentrosCosto(empresaActual.id),
        getCuentas(empresaActual.id),
        import('../services/api').then(m => m.default.get('/modelos-externos')),
        import('../services/api').then(m => m.default.get('/modelos-produccion'))
      ]);
      
      setProveedores(provRes.data);
      setCategorias(catRes.data);
      setArticulos(artRes.data);
      setLineasNegocio(lnRes.data);
      setCentrosCosto(ccRes.data);
      setCuentas(cuentasRes.data);
      // Combinar modelos externos con registros de producción
      const modelosExternosList = (modelosRes.data || []).map(m => ({ ...m, tipo: 'modelo' }));
      const modelosProdList = modelosProdRes.data || [];
      setModelosExternos([...modelosProdList, ...modelosExternosList]);
      
      // Set default moneda
      if (monedas.length > 0 && !formData.moneda_id) {
        const pen = monedas.find(m => m.codigo === 'PEN');
        if (pen) setFormData(prev => ({ ...prev, moneda_id: pen.id.toString() }));
      }
      
      // Set default linea negocio
      if (lnRes.data.length > 0) {
        setLineasCategoria(prev => prev.map(l => ({
          ...l,
          linea_negocio_id: l.linea_negocio_id || lnRes.data[0].id.toString()
        })));
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id, monedas, formData.moneda_id]);

  // Recargar modelos externos (al abrir dropdown)
  const recargarModelos = useCallback(async () => {
    try {
      const api = await import('../services/api').then(m => m.default);
      const [modelosRes, modelosProdRes] = await Promise.all([
        api.get('/modelos-externos'),
        api.get('/modelos-produccion')
      ]);
      const modelosExternosList = (modelosRes.data || []).map(m => ({ ...m, tipo: 'modelo' }));
      const modelosProdList = modelosProdRes.data || [];
      setModelosExternos([...modelosProdList, ...modelosExternosList]);
    } catch (error) {
      console.error('Error al recargar modelos:', error);
    }
  }, []);

  // Cargar compra existente para edición
  const loadCompra = useCallback(async () => {
    if (!id) return;
    
    try {
      const res = await getCompra(id);
      const data = res.data;
      
      setFormData({
        proveedor_id: data.proveedor_id?.toString() || '',
        beneficiario_txt: data.beneficiario_txt || '',
        terminos: data.terminos_pago || '',
        fecha: data.fecha,
        fecha_venc: data.fecha_venc || '',
        tipo_doc: data.tipo_doc || 'FACTURA',
        nro_doc_full: data.nro_doc_full || '',
        moneda_id: data.moneda_id?.toString() || '',
        nota: data.nota || '',
        impuestosIncluidos: data.igv_incluido !== false  // Default true si no existe
      });
      
      // Cargar líneas
      if (data.lineas && data.lineas.length > 0) {
        const catLines = data.lineas.filter(l => l.tipo_linea === 'CATEGORIA' || (l.categoria_id && !l.articulo_id));
        const artLines = data.lineas.filter(l => l.tipo_linea === 'ARTICULO' || l.articulo_id);
        
        if (catLines.length > 0) {
          setLineasCategoria(catLines.map((l, i) => ({
            id: i + 1,
            categoria_id: l.categoria_id?.toString() || '',
            descripcion: l.descripcion || '',
            linea_negocio_id: l.linea_negocio_id?.toString() || '',
            centro_costo_id: l.centro_costo_id?.toString() || '',
            importe: l.base_me || '',
            conIgv: l.afecto_igv === 1 || l.afecto_igv === true || l.igv_me > 0
          })));
        }
        
        if (artLines.length > 0) {
          setLineasArticulo(artLines.map((l, i) => ({
            id: i + 1,
            articulo_id: l.articulo_id?.toString() || '',
            descripcion: l.descripcion || '',
            cantidad: l.cant || 1,
            precio: l.tasa || '',
            linea_negocio_id: l.linea_negocio_id?.toString() || '',
            centro_costo_id: l.centro_costo_id?.toString() || '',
            conIgv: l.afecto_igv === 1 || l.afecto_igv === true || l.igv_me > 0
          })));
          setArticuloOpen(true);
        }
      }
      
      // Cargar pagos vinculados
      if (data.pagos && data.pagos.length > 0) {
        setPagosVinculados(data.pagos);
      }
      
      // Guardar estado de CxP
      setCxpEstado(data.cxp_estado || null);
      
      // Cargar letras vinculadas
      if (data.letras && data.letras.length > 0) {
        setLetrasVinculadas(data.letras);
      }
    } catch (error) {
      toast.error('Error al cargar el documento');
      navigate(-1);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadCatalogos();
  }, [loadCatalogos]);

  useEffect(() => {
    if (id && proveedores.length > 0) {
      loadCompra();
    }
  }, [id, proveedores.length, loadCompra]);

  // Resetear formulario cuando cambia de edición a nuevo
  useEffect(() => {
    if (!id) {
      // Es nuevo registro, resetear todo
      setFormData({
        proveedor_id: '',
        beneficiario_txt: '',
        terminos: '',
        fecha: new Date().toISOString().split('T')[0],
        fecha_venc: '',
        nro_doc_full: '',
        moneda_id: monedas.find(m => m.codigo === 'PEN')?.id?.toString() || '',
        nota: '',
        impuestosIncluidos: true  // Por defecto IGV incluido
      });
      setLineasCategoria([
        { id: 1, categoria_id: '', descripcion: '', linea_negocio_id: lineasNegocio[0]?.id?.toString() || '', centro_costo_id: '', importe: '', conIgv: true }
      ]);
      setLineasArticulo([]);
      setPagosVinculados([]);
    }
  }, [id, monedas, lineasNegocio]);

  // Calcular base e IGV según si impuestos están incluidos o no
  const calcularBaseIgv = (importe, conIgv, impuestosIncluidos) => {
    const monto = parseFloat(importe) || 0;
    if (!conIgv) {
      return { base: monto, igv: 0 };
    }
    if (impuestosIncluidos) {
      // Importe ya incluye IGV: base = importe / 1.18
      const base = monto / (1 + IGV_RATE);
      const igv = monto - base;
      return { base, igv };
    } else {
      // Importe es la base, IGV se suma
      const base = monto;
      const igv = monto * IGV_RATE;
      return { base, igv };
    }
  };

  // Calcular totales
  const calcularTotales = () => {
    let subtotal = 0;
    let igvTotal = 0;
    
    lineasCategoria.forEach(l => {
      const { base, igv } = calcularBaseIgv(l.importe, l.conIgv, formData.impuestosIncluidos);
      subtotal += base;
      igvTotal += igv;
    });
    
    lineasArticulo.forEach(l => {
      const importeLinea = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precio) || 0);
      const { base, igv } = calcularBaseIgv(importeLinea, l.conIgv, formData.impuestosIncluidos);
      subtotal += base;
      igvTotal += igv;
    });
    
    const total = subtotal + igvTotal;
    
    return { subtotal, igv: igvTotal, total };
  };

  // Handlers para líneas de categoría
  const addLineaCategoria = () => {
    const newId = Math.max(0, ...lineasCategoria.map(l => l.id)) + 1;
    const defaultLN = lineasNegocio[0]?.id?.toString() || '';
    setLineasCategoria([...lineasCategoria, { 
      id: newId, categoria_id: '', descripcion: '', linea_negocio_id: defaultLN, centro_costo_id: '', importe: '', conIgv: true 
    }]);
  };

  const updateLineaCategoria = (id, field, value) => {
    setLineasCategoria(lineasCategoria.map(l => 
      l.id === id ? { ...l, [field]: value } : l
    ));
  };

  const duplicateLineaCategoria = (id) => {
    const linea = lineasCategoria.find(l => l.id === id);
    if (linea) {
      const newId = Math.max(0, ...lineasCategoria.map(l => l.id)) + 1;
      setLineasCategoria([...lineasCategoria, { ...linea, id: newId }]);
    }
  };

  const removeLineaCategoria = (id) => {
    if (lineasCategoria.length > 1) {
      setLineasCategoria(lineasCategoria.filter(l => l.id !== id));
    }
  };

  const clearAllLineasCategoria = () => {
    const defaultLN = lineasNegocio[0]?.id?.toString() || '';
    setLineasCategoria([{ id: 1, categoria_id: '', descripcion: '', linea_negocio_id: defaultLN, centro_costo_id: '', importe: '', conIgv: true }]);
  };

  // Handlers para líneas de artículo
  const addLineaArticulo = () => {
    const newId = Math.max(0, ...lineasArticulo.map(l => l.id), 0) + 1;
    const defaultLN = lineasNegocio[0]?.id?.toString() || '';
    setLineasArticulo([...lineasArticulo, { 
      id: newId, articulo_id: '', descripcion: '', cantidad: 1, precio: '', linea_negocio_id: defaultLN, centro_costo_id: '', conIgv: true 
    }]);
    if (!articuloOpen) setArticuloOpen(true);
  };

  const updateLineaArticulo = (id, field, value) => {
    setLineasArticulo(lineasArticulo.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  const removeLineaArticulo = (id) => {
    setLineasArticulo(lineasArticulo.filter(l => l.id !== id));
  };

  const copyLineaArticulo = (linea) => {
    const newId = Math.max(0, ...lineasArticulo.map(l => l.id), 0) + 1;
    const copiedLinea = {
      ...linea,
      id: newId,
      cantidad: '', // Limpiar cantidad para que el usuario la ingrese
    };
    setLineasArticulo([...lineasArticulo, copiedLinea]);
    toast.success('Línea copiada');
  };

  const clearAllLineasArticulo = () => {
    setLineasArticulo([]);
  };

  // Limpiar proveedor
  const clearProveedor = () => {
    setFormData({ ...formData, proveedor_id: '' });
  };

  // Validar formulario
  const validateForm = () => {
    // Para FACTURAS, validar campos obligatorios
    if (tipo === 'FACTURA_PROVEEDOR') {
      if (!formData.proveedor_id) {
        toast.error('El proveedor es obligatorio para facturas');
        return false;
      }
      if (!formData.tipo_doc) {
        toast.error('El tipo de documento es obligatorio');
        return false;
      }
      if (!formData.fecha) {
        toast.error('La fecha de factura es obligatoria');
        return false;
      }
      if (!formData.nro_doc_full || formData.nro_doc_full.trim() === '') {
        toast.error('El número de factura es obligatorio');
        return false;
      }
    } else {
      // Para GASTOS, solo fecha es obligatoria
      if (!formData.fecha) {
        toast.error('La fecha es requerida');
        return false;
      }
    }
    
    const hasCategoria = lineasCategoria.some(l => l.categoria_id || l.descripcion || parseFloat(l.importe) > 0);
    const hasArticulo = lineasArticulo.some(l => l.articulo_id || l.descripcion || (parseFloat(l.cantidad) * parseFloat(l.precio)) > 0);
    
    if (!hasCategoria && !hasArticulo) {
      toast.error('Agregue al menos una línea de detalle');
      return false;
    }
    
    return true;
  };

  // Preparar payload
  const preparePayload = (pagosData = null) => {
    const moneda = monedas.find(m => m.id?.toString() === formData.moneda_id);
    const esPEN = moneda?.codigo === 'PEN';
    const tc = esPEN ? 1 : tcHoy.tc_venta;
    const defaultLN = lineasNegocio[0]?.id;
    
    // Preparar líneas
    const allLineas = [
      ...lineasCategoria.filter(l => l.categoria_id || l.descripcion || parseFloat(l.importe) > 0).map((l, idx) => {
        const { base, igv } = calcularBaseIgv(l.importe, l.conIgv, formData.impuestosIncluidos);
        return {
          nro_linea: idx + 1,
          tipo_linea: 'CATEGORIA',
          descripcion: l.descripcion || null,
          categoria_id: l.categoria_id ? parseInt(l.categoria_id) : null,
          articulo_id: null,
          cant: null,
          tasa: null,
          linea_negocio_id: l.linea_negocio_id ? parseInt(l.linea_negocio_id) : defaultLN,
          centro_costo_id: l.centro_costo_id ? parseInt(l.centro_costo_id) : null,
          afecto_igv: l.conIgv,
          tasa_igv: IGV_RATE,
          base_me: base,
          igv_me: igv,
          total_linea_me: base + igv,
          total_linea_pen: (base + igv) * tc
        };
      }),
      ...lineasArticulo.filter(l => l.articulo_id || l.descripcion || (parseFloat(l.cantidad) * parseFloat(l.precio)) > 0).map((l, idx) => {
        const importeLinea = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precio) || 0);
        const { base, igv } = calcularBaseIgv(importeLinea, l.conIgv, formData.impuestosIncluidos);
        return {
          nro_linea: lineasCategoria.length + idx + 1,
          tipo_linea: 'ARTICULO',
          descripcion: l.descripcion || null,
          categoria_id: null,
          articulo_id: l.articulo_id ? parseInt(l.articulo_id) : null,
          cant: parseFloat(l.cantidad) || 1,
          tasa: parseFloat(l.precio) || 0,
          linea_negocio_id: l.linea_negocio_id ? parseInt(l.linea_negocio_id) : defaultLN,
          centro_costo_id: l.centro_costo_id ? parseInt(l.centro_costo_id) : null,
          afecto_igv: l.conIgv,
          tasa_igv: IGV_RATE,
          base_me: base,
          igv_me: igv,
          total_linea_me: base + igv,
          total_linea_pen: (base + igv) * tc
        };
      })
    ];

    const payload = {
      empresa_id: empresaActual.id,
      tipo_compra: tipo,
      estado: esFactura ? 'PENDIENTE' : 'PAGADO',
      proveedor_id: formData.proveedor_id ? parseInt(formData.proveedor_id) : null,
      beneficiario_txt: formData.beneficiario_txt || null,
      fecha: formData.fecha,
      fecha_venc: formData.fecha_venc || null,
      nro_doc_full: formData.nro_doc_full || null,
      terminos_pago: formData.terminos || null,
      nota: formData.nota || null,
      moneda_id: parseInt(formData.moneda_id),
      tc: tc,
      igv_incluido: formData.impuestosIncluidos,
      lineas: allLineas
    };

    // Para gastos: agregar pagos múltiples
    if (pagosData && pagosData.length > 0) {
      payload.pagos = pagosData.filter(p => p.cuenta_id && parseFloat(p.monto) > 0).map(p => ({
        cuenta_id: parseInt(p.cuenta_id),
        medio_pago: p.medio_pago || 'TRANSFERENCIA',
        monto: parseFloat(p.monto),
        ref_pago: p.ref_pago || null
      }));
    }

    return payload;
  };

  // Handler para click en Guardar (para gastos muestra modal de pago)
  const handleSaveClick = (createNew = false) => {
    if (!validateForm()) return;
    
    // Si es edición de un gasto que ya tiene pagos, guardar directamente sin pedir pago
    if (!esFactura && isEditing && pagosVinculados.length > 0) {
      handleSubmitGastoExistente(createNew);
      return;
    }
    
    if (!esFactura) {
      // Para gastos nuevos, mostrar modal de pago
      const { total } = calcularTotales();
      const defaultRef = formData.nro_doc_full ? `Pago de ${formData.nro_doc_full}` : 'Pago de gasto';
      setPagoData({
        pagos: [{ 
          cuenta_id: cuentas.filter(c => c.activo)[0]?.id?.toString() || '', 
          medio_pago: 'TRANSFERENCIA', 
          monto: total.toFixed(2), 
          ref_pago: defaultRef 
        }]
      });
      setPendingSubmit({ createNew });
      setPagoDialogOpen(true);
    } else {
      // Para facturas, guardar directamente
      handleSubmit(createNew);
    }
  };

  // Guardar gasto existente (sin crear nuevos pagos)
  const handleSubmitGastoExistente = async (createNew = false) => {
    setSaving(true);
    const payload = preparePayload();

    try {
      await updateCompra(id, payload);
      toast.success('Gasto actualizado');
      
      if (createNew) {
        // Navegar a nuevo registro para limpiar estado de edición
        navigate(`/finanzas/compras/nuevo?tipo=${tipo}`);
      } else {
        navigate('/finanzas/compras/gasto');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Guardar factura (sin modal de pago)
  const handleSubmit = async (createNew = false) => {
    setSaving(true);
    const payload = preparePayload();

    try {
      if (isEditing) {
        await updateCompra(id, payload);
        toast.success('Documento actualizado');
      } else {
        await createCompra(payload);
        toast.success('Factura creada');
      }
      
      if (createNew) {
        // Navegar a nuevo registro para limpiar estado completamente
        navigate(`/finanzas/compras/nuevo?tipo=${tipo}`);
      } else {
        navigate(esFactura ? '/finanzas/compras/factura' : '/finanzas/compras/gasto');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Confirmar pago y guardar gasto
  const handleConfirmPago = async () => {
    // Validar que hay al menos un pago con cuenta
    const pagosValidos = pagoData.pagos.filter(p => p.cuenta_id && parseFloat(p.monto) > 0);
    if (pagosValidos.length === 0) {
      toast.error('Seleccione una cuenta y monto de pago');
      return;
    }

    // Validar que la suma de pagos cubre el total
    const { total } = calcularTotales();
    const sumaPagos = pagosValidos.reduce((sum, p) => sum + parseFloat(p.monto), 0);
    if (Math.abs(sumaPagos - total) > 0.01) {
      toast.error(`La suma de pagos (${sumaPagos.toFixed(2)}) debe ser igual al total (${total.toFixed(2)})`);
      return;
    }

    setSaving(true);
    
    // Preparar payload con todos los pagos
    const payload = preparePayload(pagoData.pagos);

    try {
      if (isEditing) {
        await updateCompra(id, payload);
        toast.success('Gasto actualizado');
      } else {
        await createCompra(payload);
        toast.success('Gasto registrado');
      }
      
      setPagoDialogOpen(false);
      
      if (pendingSubmit.createNew) {
        // Navegar a nuevo registro para limpiar estado completamente
        navigate(`/finanzas/compras/nuevo?tipo=${tipo}`);
      } else {
        navigate('/finanzas/compras/gasto');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Abrir modal para agregar pago a factura
  const handleAbrirAgregarPago = () => {
    const totalPagado = pagosVinculados.reduce((sum, p) => sum + (p.monto_aplicado_me || p.monto_me || 0), 0);
    const { total } = calcularTotales();
    const saldoPendiente = total - totalPagado;
    
    setNuevoPagoFactura({
      cuenta_id: cuentas.filter(c => c.activo)[0]?.id?.toString() || '',
      medio_pago: 'TRANSFERENCIA',
      monto: saldoPendiente.toFixed(2),
      ref_pago: `Pago de ${formData.nro_doc_full || `Factura #${id}`}`
    });
    setAgregarPagoOpen(true);
  };

  // Guardar pago de factura
  const handleGuardarPagoFactura = async () => {
    if (!nuevoPagoFactura.cuenta_id || !nuevoPagoFactura.monto || parseFloat(nuevoPagoFactura.monto) <= 0) {
      toast.error('Complete los datos del pago');
      return;
    }

    setSavingPagoFactura(true);
    try {
      await addPagoToCompra(id, {
        cuenta_id: parseInt(nuevoPagoFactura.cuenta_id),
        medio_pago: nuevoPagoFactura.medio_pago,
        monto: parseFloat(nuevoPagoFactura.monto),
        ref_pago: nuevoPagoFactura.ref_pago
      });
      toast.success('Pago registrado');
      setAgregarPagoOpen(false);
      // Recargar datos de la compra para ver el nuevo pago
      loadCompra();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setSavingPagoFactura(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData(prev => ({
      ...prev,
      proveedor_id: '',
      beneficiario_txt: '',
      nro_doc_full: '',
      nota: ''
    }));
    clearAllLineasCategoria();
    clearAllLineasArticulo();
    setPagosVinculados([]); // Limpiar pagos vinculados para nuevo registro
  };

  // Handlers para pagos múltiples
  const addPago = () => {
    const { total } = calcularTotales();
    const sumaPagos = pagoData.pagos.reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);
    const restante = Math.max(0, total - sumaPagos);
    
    setPagoData({
      pagos: [...pagoData.pagos, { 
        cuenta_id: '', 
        medio_pago: 'EFECTIVO', 
        monto: restante.toFixed(2), 
        ref_pago: '' 
      }]
    });
  };

  const updatePago = (index, field, value) => {
    const newPagos = [...pagoData.pagos];
    newPagos[index] = { ...newPagos[index], [field]: value };
    setPagoData({ pagos: newPagos });
  };

  const removePago = (index) => {
    if (pagoData.pagos.length > 1) {
      setPagoData({ pagos: pagoData.pagos.filter((_, i) => i !== index) });
    }
  };

  const { subtotal, igv, total } = calcularTotales();
  const totalPagos = pagoData.pagos.reduce((sum, p) => sum + (parseFloat(p.monto) || 0), 0);

  if (!empresaActual) {
    return <div className="flex items-center justify-center h-screen text-gray-500">Selecciona una empresa</div>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="compra-form-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Icon className="w-7 h-7 text-emerald-600" />
            <h1 className="text-xl font-semibold text-gray-900">
              {isEditing ? `Editar ${titulo}` : titulo}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              {cxpEstado === 'CANJEADO' ? (
                <>
                  <p className="text-xs text-purple-600 uppercase tracking-wide font-medium">Canjeada por Letras</p>
                  <p className="text-lg font-bold text-purple-600">Ver en módulo Letras</p>
                </>
              ) : (
                <>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Saldo Pendiente</p>
                  <p className="text-2xl font-bold text-gray-900">
                    S/. {(total - pagosVinculados.reduce((sum, p) => sum + (p.monto_aplicado_me || p.monto_me || 0), 0)).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                  </p>
                </>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Form */}
      <main className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Información Principal */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Proveedor */}
              <div className="lg:col-span-2">
                <Label className="text-sm font-medium text-gray-700">
                  Proveedor {esFactura && <span className="text-red-500">*</span>}
                </Label>
                <div className="flex gap-2 mt-1">
                  <SearchableSelect
                    options={proveedores.map(p => ({ value: p.id.toString(), label: p.razon_social }))}
                    value={formData.proveedor_id}
                    onValueChange={(v) => setFormData({ ...formData, proveedor_id: v })}
                    placeholder="Buscar proveedor..."
                    searchPlaceholder="Escriba para buscar..."
                    emptyText="No se encontró proveedor"
                    className={cn(
                      "flex-1 h-10",
                      esFactura && !formData.proveedor_id && "border-red-300 ring-1 ring-red-300"
                    )}
                  />
                  <Button variant="outline" size="icon" onClick={() => setNuevoProveedorOpen(true)} title="Crear nuevo proveedor">
                    <PlusCircle className="w-4 h-4" />
                  </Button>
                  {formData.proveedor_id && (
                    <Button variant="outline" size="icon" onClick={clearProveedor} title="Limpiar proveedor">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Beneficiario (solo si no hay proveedor) */}
              {!formData.proveedor_id && (
                <div className="lg:col-span-2">
                  <Label className="text-sm font-medium text-gray-700">O escribir beneficiario</Label>
                  <Input
                    className="mt-1"
                    value={formData.beneficiario_txt}
                    onChange={(e) => setFormData({ ...formData, beneficiario_txt: e.target.value })}
                    placeholder="Nombre del beneficiario"
                  />
                </div>
              )}

              {/* Términos */}
              <div>
                <Label className="text-sm font-medium text-gray-700">Términos</Label>
                <Input
                  className="mt-1"
                  value={formData.terminos}
                  onChange={(e) => setFormData({ ...formData, terminos: e.target.value })}
                  placeholder="Ej: 30 días"
                />
              </div>

              {/* Moneda */}
              <div>
                <Label className="text-sm font-medium text-gray-700">Moneda</Label>
                <Select value={formData.moneda_id} onValueChange={(v) => setFormData({ ...formData, moneda_id: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Moneda" />
                  </SelectTrigger>
                  <SelectContent>
                    {monedas.map(m => (
                      <SelectItem key={m.id} value={m.id.toString()}>{m.codigo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Fechas */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Fecha de {esFactura ? 'factura' : 'gasto'} {esFactura && <span className="text-red-500">*</span>}
                </Label>
                <Input
                  type="date"
                  className={cn("mt-1", esFactura && !formData.fecha && "border-red-300")}
                  value={formData.fecha}
                  onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                />
              </div>

              {esFactura && (
                <div>
                  <Label className="text-sm font-medium text-gray-700">Fecha de vencimiento</Label>
                  <Input
                    type="date"
                    className="mt-1"
                    value={formData.fecha_venc}
                    onChange={(e) => setFormData({ ...formData, fecha_venc: e.target.value })}
                  />
                </div>
              )}

              {/* Tipo de documento */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Tipo de documento {esFactura && <span className="text-red-500">*</span>}
                </Label>
                <Select value={formData.tipo_doc} defaultValue="FACTURA" onValueChange={(v) => setFormData({ ...formData, tipo_doc: v })}>
                  <SelectTrigger className={cn("mt-1", esFactura && !formData.tipo_doc && "border-red-300")}>
                    <SelectValue placeholder="Seleccionar tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {tiposDocumento.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Número de documento */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  N.º de {formData.tipo_doc === 'FACTURA' ? 'factura' : formData.tipo_doc === 'BOLETA' ? 'boleta' : 'documento'} {esFactura && <span className="text-red-500">*</span>}
                </Label>
                <Input
                  className={cn("mt-1", esFactura && !formData.nro_doc_full && "border-red-300")}
                  value={formData.nro_doc_full}
                  onChange={(e) => setFormData({ ...formData, nro_doc_full: e.target.value })}
                  placeholder={formData.tipo_doc === 'FACTURA' ? 'F001-00001' : formData.tipo_doc === 'BOLETA' ? 'B001-00001' : 'NV001-00001'}
                />
              </div>
            </div>
          </div>

          {/* Detalles de la categoría */}
          <Collapsible open={categoriaOpen} onOpenChange={setCategoriaOpen}>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                <CollapsibleTrigger asChild>
                  <button className="flex items-center gap-2">
                    {categoriaOpen ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                    <span className="font-medium text-gray-900">Detalles de la categoría</span>
                    <span className="text-sm text-gray-500 ml-2">({lineasCategoria.length} línea{lineasCategoria.length !== 1 ? 's' : ''})</span>
                  </button>
                </CollapsibleTrigger>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>Los importes son</span>
                  <Select 
                    value={formData.impuestosIncluidos ? "incluidos" : "no_incluidos"} 
                    onValueChange={(v) => setFormData({ ...formData, impuestosIncluidos: v === "incluidos" })}
                  >
                    <SelectTrigger className="w-48 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no_incluidos">Impuestos no incluidos</SelectItem>
                      <SelectItem value="incluidos">Impuestos incluidos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <CollapsibleContent>
                <div className="border-t border-gray-200">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="col-span-1">#</div>
                    <div className="col-span-2">Categoría</div>
                    <div className="col-span-3">Descripción</div>
                    <div className="col-span-2">Línea Negocio</div>
                    <div className="col-span-1 text-right">Importe</div>
                    <div className="col-span-1 text-center">IGV 18%</div>
                    <div className="col-span-2 text-right">Acciones</div>
                  </div>
                  
                  {/* Table Rows */}
                  <div className="divide-y divide-gray-100">
                    {lineasCategoria.map((linea, index) => (
                      <div key={linea.id} className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-gray-50">
                        <div className="col-span-1 text-gray-500 font-medium">{index + 1}</div>
                        <div className="col-span-2">
                          <TreeCategorySelect
                            value={linea.categoria_id}
                            onValueChange={(v) => updateLineaCategoria(linea.id, 'categoria_id', v)}
                            categorias={categorias}
                            placeholder="Categoría"
                          />
                        </div>
                        <div className="col-span-3">
                          <Input
                            className="h-9"
                            value={linea.descripcion}
                            onChange={(e) => updateLineaCategoria(linea.id, 'descripcion', e.target.value)}
                            placeholder="Descripción"
                          />
                        </div>
                        <div className="col-span-2">
                          <Select value={linea.linea_negocio_id} onValueChange={(v) => updateLineaCategoria(linea.id, 'linea_negocio_id', v)}>
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Línea" />
                            </SelectTrigger>
                            <SelectContent>
                              {lineasNegocio.map(ln => (
                                <SelectItem key={ln.id} value={ln.id.toString()}>{ln.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-1">
                          <Input
                            type="number"
                            step="0.01"
                            className="h-9 text-right"
                            value={linea.importe}
                            onChange={(e) => updateLineaCategoria(linea.id, 'importe', e.target.value)}
                            placeholder="0.00"
                          />
                        </div>
                        <div className="col-span-1 flex justify-center">
                          <Checkbox
                            checked={linea.conIgv}
                            onCheckedChange={(checked) => updateLineaCategoria(linea.id, 'conIgv', checked)}
                          />
                        </div>
                        <div className="col-span-2 flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => duplicateLineaCategoria(linea.id)}>
                            <Copy className="w-4 h-4 text-gray-400" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8" 
                            onClick={() => removeLineaCategoria(linea.id)}
                            disabled={lineasCategoria.length === 1}
                          >
                            <Trash2 className="w-4 h-4 text-gray-400" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Actions */}
                  <div className="px-4 py-3 border-t border-gray-200 flex gap-3">
                    <Button variant="outline" size="sm" onClick={addLineaCategoria}>
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar línea
                    </Button>
                    <Button variant="outline" size="sm" onClick={clearAllLineasCategoria}>
                      Borrar todas las líneas
                    </Button>
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          {/* Detalles del artículo */}
          <Collapsible open={articuloOpen} onOpenChange={setArticuloOpen}>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <CollapsibleTrigger asChild>
                <button className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-2">
                    {articuloOpen ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
                    <span className="font-medium text-gray-900">Detalles del artículo</span>
                    {lineasArticulo.length > 0 && (
                      <span className="text-sm text-gray-500 ml-2">({lineasArticulo.length} artículo{lineasArticulo.length !== 1 ? 's' : ''})</span>
                    )}
                  </div>
                </button>
              </CollapsibleTrigger>
              
              <CollapsibleContent>
                <div className="border-t border-gray-200">
                  {lineasArticulo.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <p className="mb-4">No hay artículos agregados</p>
                      <Button variant="outline" onClick={addLineaArticulo}>
                        <Plus className="w-4 h-4 mr-1" />
                        Agregar artículo
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Table Header */}
                      <div className="grid gap-2 px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider" style={{gridTemplateColumns: '40px 180px 130px 50px 70px 80px 140px 90px 50px 50px'}}>
                        <div>#</div>
                        <div>Artículo</div>
                        <div>Modelo</div>
                        <div>Und</div>
                        <div className="text-right">Cant.</div>
                        <div className="text-right">Precio</div>
                        <div>Línea Negocio</div>
                        <div className="text-right">Importe</div>
                        <div className="text-center">IGV</div>
                        <div></div>
                      </div>
                      
                      {/* Table Rows */}
                      <div className="divide-y divide-gray-100">
                        {lineasArticulo.map((linea, index) => {
                          const importeLinea = (parseFloat(linea.cantidad) || 0) * (parseFloat(linea.precio) || 0);
                          const articuloSeleccionado = articulos.find(a => a.id.toString() === linea.articulo_id);
                          const modeloSeleccionado = modelosExternos.find(m => m.id.toString() === linea.modelo_id);
                          return (
                            <div key={linea.id} className="grid gap-2 px-4 py-3 items-center hover:bg-gray-50" style={{gridTemplateColumns: '40px 180px 130px 50px 70px 80px 140px 90px 50px 50px'}}>
                              <div className="text-gray-500 font-medium">{index + 1}</div>
                              <div className="flex gap-1 overflow-hidden">
                                <SearchableSelect
                                  key={`art-${linea.id}`}
                                  options={articulos.map(a => ({ value: String(a.id), label: a.nombre }))}
                                  value={linea.articulo_id || ''}
                                  onValueChange={(v) => {
                                    const art = articulos.find(a => String(a.id) === v);
                                    setLineasArticulo(prev => prev.map(l => {
                                      if (l.id !== linea.id) return l;
                                      return {
                                        ...l,
                                        articulo_id: v,
                                        descripcion: art?.nombre || '',
                                        precio: art?.precio_ref || '',
                                        unidad: art?.unidad || 'UND',
                                        // Auto-completar categoría y línea de negocio del artículo
                                        categoria_id: art?.categoria_id?.toString() || l.categoria_id,
                                        linea_negocio_id: art?.linea_negocio_id_def?.toString() || l.linea_negocio_id
                                      };
                                    }));
                                  }}
                                  placeholder="Artículo"
                                  searchPlaceholder="Buscar artículo..."
                                  emptyText="No encontrado"
                                  className="h-9 flex-1 min-w-0"
                                />
                                <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => setNuevoArticuloOpen(true)} title="Crear artículo">
                                  <PlusCircle className="w-4 h-4" />
                                </Button>
                              </div>
                              <div className="overflow-hidden">
                                <SearchableSelect
                                  key={`mod-${linea.id}-${linea.modelo_id}`}
                                  options={modelosExternos.map(m => ({ 
                                    value: m.id.toString(), 
                                    label: m.nombre 
                                  }))}
                                  value={linea.modelo_id || ''}
                                  onValueChange={(v) => updateLineaArticulo(linea.id, 'modelo_id', v)}
                                  placeholder="Modelo / Corte"
                                  searchPlaceholder="Buscar modelo o corte..."
                                  emptyText="No encontrado"
                                  className="h-9 text-xs"
                                  onOpen={recargarModelos}
                                />
                              </div>
                              <div>
                                <span className="text-xs text-gray-500">{articuloSeleccionado?.unidad || linea.unidad || 'UND'}</span>
                              </div>
                              <div>
                                <Input
                                  type="number"
                                  step="0.01"
                                  className="h-9 text-right"
                                  value={linea.cantidad}
                                  onChange={(e) => updateLineaArticulo(linea.id, 'cantidad', e.target.value)}
                                  placeholder="1"
                                />
                              </div>
                              <div>
                                <Input
                                  type="number"
                                  step="0.01"
                                  className="h-9 text-right"
                                  value={linea.precio}
                                  onChange={(e) => updateLineaArticulo(linea.id, 'precio', e.target.value)}
                                  placeholder="0.00"
                                />
                              </div>
                              <div className="overflow-hidden">
                                <Select value={linea.linea_negocio_id} onValueChange={(v) => updateLineaArticulo(linea.id, 'linea_negocio_id', v)}>
                                  <SelectTrigger className="h-9">
                                    <SelectValue placeholder="Línea" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {lineasNegocio.map(ln => (
                                      <SelectItem key={ln.id} value={ln.id.toString()}>{ln.nombre}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Input
                                  type="number"
                                  className="h-9 text-right bg-gray-50"
                                  value={importeLinea.toFixed(2)}
                                  readOnly
                                />
                              </div>
                              <div className="flex justify-center">
                                <Checkbox
                                  checked={linea.conIgv}
                                  onCheckedChange={(checked) => updateLineaArticulo(linea.id, 'conIgv', checked)}
                                />
                              </div>
                              <div className="flex justify-end gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8" 
                                  onClick={() => copyLineaArticulo(linea)}
                                  title="Copiar línea"
                                >
                                  <Copy className="w-4 h-4 text-blue-400 hover:text-blue-600" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8" 
                                  onClick={() => removeLineaArticulo(linea.id)}
                                  title="Eliminar línea"
                                >
                                  <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Actions */}
                      <div className="px-4 py-3 border-t border-gray-200 flex gap-3">
                        <Button variant="outline" size="sm" onClick={addLineaArticulo}>
                          <Plus className="w-4 h-4 mr-1" />
                          Agregar artículo
                        </Button>
                        <Button variant="outline" size="sm" onClick={clearAllLineasArticulo}>
                          Borrar todos los artículos
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          {/* Pagos Vinculados (solo en edición) */}
          {isEditing && (pagosVinculados.length > 0 || esFactura) && (
            <div className="bg-white rounded-xl shadow-sm border border-emerald-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-semibold text-gray-900">Pagos Vinculados</h3>
                  {pagosVinculados.length > 0 && (
                    <span className="text-sm text-gray-500">({pagosVinculados.length})</span>
                  )}
                </div>
                {/* Botón agregar pago solo para facturas con saldo pendiente y NO canjeadas */}
                {esFactura && cxpEstado !== 'CANJEADO' && (() => {
                  const totalPagado = pagosVinculados.reduce((sum, p) => sum + (p.monto_aplicado_me || p.monto_me || 0), 0);
                  const saldoPendiente = total - totalPagado;
                  return saldoPendiente > 0.01;
                })() && (
                  <Button 
                    onClick={handleAbrirAgregarPago} 
                    size="sm" 
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <PlusCircle className="w-4 h-4 mr-1" />
                    Agregar Pago
                  </Button>
                )}
                {/* Mensaje si está canjeada */}
                {cxpEstado === 'CANJEADO' && (
                  <span className="text-sm text-purple-600 font-medium bg-purple-50 px-3 py-1 rounded-full">
                    Canjeada por Letras - Pagar en módulo de Letras
                  </span>
                )}
              </div>
              
              {pagosVinculados.length > 0 ? (
                <>
                  <div className="space-y-2">
                    {pagosVinculados.map((pago, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="text-sm">
                            <span className="font-medium">{pago.fecha}</span>
                            <span className="text-gray-400 mx-2">|</span>
                            <span className="text-gray-600">{pago.cuenta_nombre}</span>
                            <span className="text-gray-400 mx-2">|</span>
                            <span className="text-gray-500">{pago.medio_pago}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-emerald-600">
                            {pago.moneda_codigo} {(pago.monto_aplicado_me || pago.monto_me)?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                          </span>
                          {pago.ref_pago && (
                            <p className="text-xs text-gray-500">Ref: {pago.ref_pago}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t flex justify-between">
                    <span className="font-medium text-gray-700">Total Pagado</span>
                    <span className="font-bold text-emerald-600">
                      S/. {pagosVinculados.reduce((sum, p) => sum + (p.monto_aplicado_me || p.monto_me || 0), 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <CreditCard className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p>No hay pagos registrados</p>
                </div>
              )}
            </div>
          )}

          {/* Letras Vinculadas (solo si hay letras) */}
          {isEditing && letrasVinculadas.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-purple-200 p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-gray-900">Letras Vinculadas</h3>
                  <span className="text-sm text-gray-500">({letrasVinculadas.length})</span>
                </div>
                <button
                  onClick={() => navigate('/letras')}
                  className="text-sm text-purple-600 hover:text-purple-800 hover:underline"
                >
                  Ver módulo de Letras →
                </button>
              </div>
              
              <div className="space-y-2">
                {letrasVinculadas.map((letra, idx) => {
                  const isPagada = letra.estado === 'PAGADO' || letra.saldo_pendiente <= 0.01;
                  const isParcial = letra.estado === 'PARCIAL' || (letra.total_pagado_me > 0 && letra.saldo_pendiente > 0.01);
                  const isVencida = !isPagada && new Date(letra.fecha_venc) < new Date();
                  
                  return (
                    <div 
                      key={letra.id || idx}
                      className={cn(
                        "flex items-center justify-between p-3 rounded-lg border",
                        isPagada ? "bg-emerald-50 border-emerald-200" :
                        isParcial ? "bg-amber-50 border-amber-200" :
                        isVencida ? "bg-red-50 border-red-200" :
                        "bg-gray-50 border-gray-200"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {isPagada ? (
                          <CheckCircle className="w-5 h-5 text-emerald-500" />
                        ) : isParcial ? (
                          <Clock className="w-5 h-5 text-amber-500" />
                        ) : isVencida ? (
                          <AlertCircle className="w-5 h-5 text-red-500" />
                        ) : (
                          <Clock className="w-5 h-5 text-gray-400" />
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{letra.nro_letra}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Calendar className="w-3 h-3" />
                            <span>Vence: {letra.fecha_venc}</span>
                            {letra.aceptante && (
                              <>
                                <span className="text-gray-300">|</span>
                                <span>{letra.aceptante}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={cn(
                          "font-bold",
                          isPagada ? "text-emerald-600" :
                          isParcial ? "text-amber-600" :
                          isVencida ? "text-red-600" :
                          "text-gray-900"
                        )}>
                          {letra.moneda_codigo} {letra.monto_me?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                        </p>
                        <p className="text-xs">
                          {isPagada ? (
                            <span className="text-emerald-600 font-medium">✓ Pagada</span>
                          ) : isParcial ? (
                            <span className="text-amber-600">Pendiente: {letra.moneda_codigo} {letra.saldo_pendiente?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                          ) : isVencida ? (
                            <span className="text-red-600 font-medium">Vencida</span>
                          ) : (
                            <span className="text-gray-500">Pendiente</span>
                          )}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Resumen de Letras */}
              <div className="mt-4 pt-4 border-t border-purple-200 grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Total Letras</p>
                  <p className="font-bold text-gray-900">
                    S/. {letrasVinculadas.reduce((sum, l) => sum + (l.monto_me || 0), 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Pagado</p>
                  <p className="font-bold text-emerald-600">
                    S/. {letrasVinculadas.reduce((sum, l) => sum + (l.total_pagado_me || 0), 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Pendiente</p>
                  <p className="font-bold text-orange-600">
                    S/. {letrasVinculadas.reduce((sum, l) => sum + (l.saldo_pendiente || 0), 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Nota y Totales */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <Label className="text-sm font-medium text-gray-700">Nota</Label>
              <Textarea
                className="mt-2 min-h-[100px]"
                value={formData.nota}
                onChange={(e) => setFormData({ ...formData, nota: e.target.value })}
                placeholder="Añadir una nota..."
              />
            </div>
            
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">S/. {subtotal.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">IGV (18%)</span>
                  <span className="font-medium">S/. {igv.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="border-t pt-3 flex justify-between">
                  <span className="font-semibold text-gray-900">Total</span>
                  <span className="text-xl font-bold text-gray-900">S/. {total.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer Actions */}
      <footer className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Button variant="outline" onClick={() => navigate(-1)} className="text-gray-600">
            Cancelar
          </Button>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={() => handleSaveClick(false)}
              disabled={saving}
              className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
            >
              <Save className="w-4 h-4 mr-2" />
              Guardar
            </Button>
            <Button 
              onClick={() => handleSaveClick(true)}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? 'Guardando...' : 'Guardar y crear nueva'}
            </Button>
          </div>
        </div>
      </footer>

      {/* Modal de Pago para Gastos */}
      <Dialog open={pagoDialogOpen} onOpenChange={setPagoDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Registrar Pago</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total del gasto</span>
                <span className="font-bold text-lg">S/. {total.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>

            <div className="space-y-3">
              {pagoData.pagos.map((pago, index) => (
                <div key={index} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Pago {index + 1}</span>
                    {pagoData.pagos.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removePago(index)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Cuenta *</Label>
                      <Select value={pago.cuenta_id} onValueChange={(v) => updatePago(index, 'cuenta_id', v)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {cuentas.filter(c => c.activo).map(c => (
                            <SelectItem key={c.id} value={c.id.toString()}>
                              {c.nombre} ({c.moneda_codigo})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Medio de pago</Label>
                      <Select value={pago.medio_pago} onValueChange={(v) => updatePago(index, 'medio_pago', v)}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MEDIOS_PAGO.map(m => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Monto</Label>
                      <Input
                        type="number"
                        step="0.01"
                        className="mt-1"
                        value={pago.monto}
                        onChange={(e) => updatePago(index, 'monto', e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Referencia</Label>
                      <Input
                        className="mt-1"
                        value={pago.ref_pago}
                        onChange={(e) => updatePago(index, 'ref_pago', e.target.value)}
                        placeholder="Nro. operación"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <Button variant="outline" size="sm" onClick={addPago} className="w-full">
              <Plus className="w-4 h-4 mr-1" />
              Agregar otro medio de pago
            </Button>

            {/* Resumen */}
            <div className={cn(
              "rounded-lg p-4 space-y-2",
              Math.abs(totalPagos - total) < 0.01 ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"
            )}>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total del gasto</span>
                <span className="font-medium">S/. {total.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total pagos</span>
                <span className={cn(
                  "font-medium",
                  Math.abs(totalPagos - total) < 0.01 ? "text-emerald-600" : "text-red-600"
                )}>
                  S/. {totalPagos.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                </span>
              </div>
              {Math.abs(totalPagos - total) >= 0.01 && (
                <div className="flex justify-between text-sm pt-2 border-t border-red-200">
                  <span className="text-red-600 font-semibold">
                    {totalPagos < total ? '⚠️ Falta pagar' : '⚠️ Excede el total'}
                  </span>
                  <span className="font-bold text-red-600">
                    S/. {Math.abs(total - totalPagos).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}
              {Math.abs(totalPagos - total) < 0.01 && (
                <div className="flex justify-between text-sm pt-2 border-t border-emerald-200">
                  <span className="text-emerald-600 font-semibold">✓ Pago completo</span>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPagoDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmPago}
              disabled={saving || Math.abs(totalPagos - total) >= 0.01 || !pagoData.pagos.some(p => p.cuenta_id && parseFloat(p.monto) > 0)}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300"
            >
              {saving ? 'Guardando...' : 'Confirmar y Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Agregar Pago (para facturas) */}
      <Dialog open={agregarPagoOpen} onOpenChange={setAgregarPagoOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-emerald-600" />
              Agregar Pago
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Info del documento */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-gray-900">{formData.nro_doc_full || `Factura #${id}`}</p>
                  <p className="text-sm text-gray-500">
                    {proveedores.find(p => p.id?.toString() === formData.proveedor_id)?.razon_social || formData.beneficiario_txt}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Saldo Pendiente</p>
                  <p className="font-bold text-lg text-orange-600">
                    S/. {(total - pagosVinculados.reduce((sum, p) => sum + (p.monto_aplicado_me || p.monto_me || 0), 0)).toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>

            {/* Formulario de pago */}
            <div className="space-y-4">
              <div>
                <Label>Cuenta *</Label>
                <Select value={nuevoPagoFactura.cuenta_id} onValueChange={(v) => setNuevoPagoFactura({...nuevoPagoFactura, cuenta_id: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccione cuenta" />
                  </SelectTrigger>
                  <SelectContent>
                    {cuentas.filter(c => c.activo).map(c => (
                      <SelectItem key={c.id} value={c.id.toString()}>
                        {c.nombre} ({c.moneda_codigo})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Medio de Pago *</Label>
                <Select value={nuevoPagoFactura.medio_pago} onValueChange={(v) => setNuevoPagoFactura({...nuevoPagoFactura, medio_pago: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDIOS_PAGO.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Monto *</Label>
                <Input
                  type="number"
                  step="0.01"
                  className="mt-1"
                  value={nuevoPagoFactura.monto}
                  onChange={(e) => setNuevoPagoFactura({...nuevoPagoFactura, monto: e.target.value})}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label>Referencia</Label>
                <Input
                  className="mt-1"
                  value={nuevoPagoFactura.ref_pago}
                  onChange={(e) => setNuevoPagoFactura({...nuevoPagoFactura, ref_pago: e.target.value})}
                  placeholder="Nro. de operación, cheque, etc."
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAgregarPagoOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleGuardarPagoFactura} 
              disabled={savingPagoFactura}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingPagoFactura ? 'Guardando...' : 'Registrar Pago'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Crear Proveedor */}
      <Dialog open={nuevoProveedorOpen} onOpenChange={setNuevoProveedorOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-indigo-600" />
              Crear Nuevo Proveedor
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo Doc</Label>
                <Select value={nuevoProveedor.tipo_doc} onValueChange={(v) => setNuevoProveedor({...nuevoProveedor, tipo_doc: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RUC">RUC</SelectItem>
                    <SelectItem value="DNI">DNI</SelectItem>
                    <SelectItem value="CE">CE</SelectItem>
                    <SelectItem value="OTRO">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nro Documento</Label>
                <Input className="mt-1" value={nuevoProveedor.nro_doc} onChange={(e) => setNuevoProveedor({...nuevoProveedor, nro_doc: e.target.value})} placeholder="20123456789" />
              </div>
            </div>
            <div>
              <Label>Razón Social *</Label>
              <Input className="mt-1" value={nuevoProveedor.razon_social} onChange={(e) => setNuevoProveedor({...nuevoProveedor, razon_social: e.target.value})} placeholder="Nombre del proveedor" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Teléfono</Label>
                <Input className="mt-1" value={nuevoProveedor.telefono} onChange={(e) => setNuevoProveedor({...nuevoProveedor, telefono: e.target.value})} />
              </div>
              <div>
                <Label>Email</Label>
                <Input className="mt-1" value={nuevoProveedor.email} onChange={(e) => setNuevoProveedor({...nuevoProveedor, email: e.target.value})} type="email" />
              </div>
            </div>
            <div>
              <Label>Dirección</Label>
              <Input className="mt-1" value={nuevoProveedor.direccion} onChange={(e) => setNuevoProveedor({...nuevoProveedor, direccion: e.target.value})} />
            </div>
            <div className="w-32">
              <Label>Días Crédito</Label>
              <Input className="mt-1" type="number" value={nuevoProveedor.dias_credito} onChange={(e) => setNuevoProveedor({...nuevoProveedor, dias_credito: parseInt(e.target.value) || 30})} />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setNuevoProveedorOpen(false)}>Cancelar</Button>
            <Button onClick={async () => {
              if (!nuevoProveedor.razon_social) { toast.error('Ingrese razón social'); return; }
              setSavingProveedor(true);
              try {
                const res = await import('../services/api').then(m => m.default.post('/proveedores', { ...nuevoProveedor, empresa_id: empresaActual.id }));
                const newProv = res.data;
                setProveedores([...proveedores, newProv]);
                setFormData({...formData, proveedor_id: newProv.id.toString()});
                setNuevoProveedorOpen(false);
                setNuevoProveedor({ tipo_doc: 'RUC', nro_doc: '', razon_social: '', telefono: '', email: '', direccion: '', dias_credito: 30 });
                toast.success('Proveedor creado');
              } catch (e) { toast.error('Error al crear proveedor'); }
              setSavingProveedor(false);
            }} disabled={savingProveedor} className="bg-indigo-600 hover:bg-indigo-700">
              {savingProveedor ? 'Guardando...' : 'Crear Proveedor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Crear Artículo */}
      <Dialog open={nuevoArticuloOpen} onOpenChange={setNuevoArticuloOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-indigo-600" />
              Crear Nuevo Artículo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código (N° Corte)</Label>
                <Input className="mt-1" value={nuevoArticulo.codigo} onChange={(e) => setNuevoArticulo({...nuevoArticulo, codigo: e.target.value})} placeholder="001" />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={nuevoArticulo.tipo} onValueChange={(v) => setNuevoArticulo({...nuevoArticulo, tipo: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PRODUCTO">Producto</SelectItem>
                    <SelectItem value="SERVICIO">Servicio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Nombre *</Label>
              <Input className="mt-1" value={nuevoArticulo.nombre} onChange={(e) => setNuevoArticulo({...nuevoArticulo, nombre: e.target.value})} placeholder="Nombre del artículo" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Unidad</Label>
                <Select value={nuevoArticulo.unidad} onValueChange={(v) => setNuevoArticulo({...nuevoArticulo, unidad: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UND">UND</SelectItem>
                    <SelectItem value="KG">KG</SelectItem>
                    <SelectItem value="M">M</SelectItem>
                    <SelectItem value="M2">M2</SelectItem>
                    <SelectItem value="ROLLO">ROLLO</SelectItem>
                    <SelectItem value="CAJA">CAJA</SelectItem>
                    <SelectItem value="PAR">PAR</SelectItem>
                    <SelectItem value="JGO">JGO</SelectItem>
                    <SelectItem value="GLB">GLB</SelectItem>
                    <SelectItem value="HRS">HRS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Precio Ref.</Label>
                <Input className="mt-1" type="number" step="0.01" value={nuevoArticulo.precio_ref} onChange={(e) => setNuevoArticulo({...nuevoArticulo, precio_ref: e.target.value})} placeholder="0.00" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoría</Label>
                <div className="mt-1">
                  <TreeCategorySelect
                    value={nuevoArticulo.categoria_id}
                    onValueChange={(v) => setNuevoArticulo({...nuevoArticulo, categoria_id: v})}
                    categorias={categorias}
                    placeholder="Opcional"
                  />
                </div>
              </div>
              <div>
                <Label>Línea Negocio</Label>
                <Select value={nuevoArticulo.linea_negocio_id_def} onValueChange={(v) => setNuevoArticulo({...nuevoArticulo, linea_negocio_id_def: v})}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Opcional" /></SelectTrigger>
                  <SelectContent>
                    {lineasNegocio.map(ln => <SelectItem key={ln.id} value={ln.id.toString()}>{ln.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setNuevoArticuloOpen(false)}>Cancelar</Button>
            <Button onClick={async () => {
              if (!nuevoArticulo.nombre) { toast.error('Ingrese nombre del artículo'); return; }
              setSavingArticulo(true);
              try {
                const payload = {
                  empresa_id: empresaActual.id,
                  codigo: nuevoArticulo.codigo || null,
                  nombre: nuevoArticulo.nombre,
                  tipo: nuevoArticulo.tipo,
                  unidad: nuevoArticulo.unidad,
                  precio_ref: nuevoArticulo.precio_ref ? parseFloat(nuevoArticulo.precio_ref) : null,
                  categoria_id: nuevoArticulo.categoria_id ? parseInt(nuevoArticulo.categoria_id) : null,
                  linea_negocio_id_def: nuevoArticulo.linea_negocio_id_def ? parseInt(nuevoArticulo.linea_negocio_id_def) : null,
                  activo: true
                };
                const res = await import('../services/api').then(m => m.default.post('/articulos', payload));
                const newArt = res.data;
                setArticulos([...articulos, newArt]);
                setNuevoArticuloOpen(false);
                setNuevoArticulo({ codigo: '', nombre: '', tipo: 'PRODUCTO', unidad: 'UND', precio_ref: '', categoria_id: '', linea_negocio_id_def: '' });
                toast.success('Artículo creado');
              } catch (e) { toast.error('Error al crear artículo'); }
              setSavingArticulo(false);
            }} disabled={savingArticulo} className="bg-indigo-600 hover:bg-indigo-700">
              {savingArticulo ? 'Guardando...' : 'Crear Artículo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompraFormPage;
